// PlanSerice.js — migrated to the OpenAPI-generated client (admin/program).
//
// Adapter-first: each exported function keeps its EXACT pre-migration signature and return
// shape so callers are untouched. The legacy code returned the raw axios response and callers
// read the payload off `res.data` (these /admin/program endpoints return the payload object
// directly, NOT the {statusCode,errorMessage,body} envelope — the one exception is
// getWeekStatistics, whose caller unwraps `res.data.body ?? res.data`). The SDK returns
// {data, error} where `data` is the parsed JSON body, so we return `{data}` to keep `.data`
// reads working 1:1. Auth + X-Timezone are injected by configureApi's interceptor, so the
// per-call `localStorage.getItem("auth")` reads are gone.
import {client} from "../api/generated/client.gen";
import {
    adminProgramControllerGetWeek,
    adminProgramControllerGetWorkout,
    adminProgramControllerGetActiveProgram,
    adminProgramControllerGetProgramById,
    adminProgramControllerCreateAdditionalBlock,
    adminProgramControllerDeleteBlock,
    adminProgramControllerGetHistory,
    adminProgramControllerChangeTraineeProgram,
    adminProgramControllerUpdateWorkout,
    adminProgramControllerGetPendingPrograms,
    adminProgramControllerApprovePending,
    adminProgramControllerFinishWorkout,
    adminProgramControllerResetWeek,
    adminProgramControllerRunManipulation,
    adminProgramControllerBulkChangeTraineeProgram,
    adminProgramControllerGetWeekStatistics,
    adminProgramControllerUpdateProgramSettings,
    adminProgramControllerUpdateProgramWarmup,
    adminProgramControllerGetBulkAssignCount,
    adminProgramControllerBulkAssignToAllFiltered,
    adminProgramControllerAddWorkoutDay,
    adminProgramControllerRemoveWorkoutDay,
    adminProgramControllerMoveWorkoutDay,
} from '../api/generated';

// Re-throw an SDK error as an axios-shaped error so existing catch handlers that read
// `e.response?.data?.errorCode` / `.message` keep working exactly as they did on axios.
const asAxiosError = (error, response) => {
    /** @type {any} */
    const err = new Error(error?.errorMessage || error?.message || 'Request failed');
    err.response = {data: error, status: response?.status};
    return err;
};

export const getWeek = async (weekId) => {
    const {data, error, response} = await adminProgramControllerGetWeek({path: {weekId}});
    if (error) throw asAxiosError(error, response);
    return {data};
};

export const getWorkout = async (workoutId) => {
    const {data, error, response} = await adminProgramControllerGetWorkout({path: {workoutId}});
    if (error) throw asAxiosError(error, response);
    return {data};
};

export const getCurrentPlan = async (traineeId) => {
    const {data, error, response} = await adminProgramControllerGetActiveProgram({path: {traineeId}});
    if (error) throw asAxiosError(error, response);
    return {data};
};

export const getPlan = async (traineeId, programId) => {
    const {data, error, response} = await adminProgramControllerGetProgramById({path: {traineeId, programId}});
    if (error) throw asAxiosError(error, response);
    return {data};
};

export const addNewBlock = async (programId) => {
    const {data, error, response} = await adminProgramControllerCreateAdditionalBlock({path: {programId}});
    if (error) throw asAxiosError(error, response);
    return {data};
};

/**
 * Append one week to the end of a block.
 *
 * Hand-written rather than generated: the endpoint post-dates the current openapi snapshot.
 * Regenerate the SDK and swap this for `adminProgramControllerAppendWeekToBlock` when the
 * snapshot is next refreshed.
 */
export const appendWeekToBlock = async (blockId) => {
    const {data, error, response} = await client.post({url: `/admin/program/block/${blockId}/week`});
    if (error) throw asAxiosError(error, response);
    return {data};
};

export const deleteBlock = async (blockId) => {
    const {data, error, response} = await adminProgramControllerDeleteBlock({path: {blockId}});
    if (error) throw asAxiosError(error, response);
    return {data};
};

export const getHistory = async (traineeId) => {
    const {data, error, response} = await adminProgramControllerGetHistory({path: {traineeId}});
    if (error) throw asAxiosError(error, response);
    return {data};
};

/**
 * @param {number} traineeId
 * @param {number} templateId
 * @param {boolean} [includeRmWeek]
 * @param {{automatic?: boolean, useProgressionAlgorithm?: boolean, useRir?: boolean} | null} [advancedSettings]
 */
export const changeProgram = async (traineeId, templateId, includeRmWeek = false, advancedSettings = null) => {
    // The old code sent all settings in the BODY. The SDK models the body as ProgramSettings
    // (which includes includeRmWeek) plus an optional includeRmWeek query param; we keep sending
    // everything in the body to match the server contract the app has always used.
    const body = {
        includeRmWeek: includeRmWeek,
        automaticProgression: advancedSettings?.automatic ?? true,
        useProgressionAlgorithm: advancedSettings?.useProgressionAlgorithm ?? true,
        showRir: advancedSettings?.useRir ?? true,
    };
    const {data, error, response} = await adminProgramControllerChangeTraineeProgram({
        path: {traineeId, templateId},
        body,
    });
    if (error) throw asAxiosError(error, response);
    return {data};
};

export const updateProgram = async (body) => {
    const {data, error, response} = await adminProgramControllerUpdateWorkout({body});
    if (error) throw asAxiosError(error, response);
    return {data};
};

export const getPendingPrograms = async (companyId, keyword, page = 0, trainer, sortKey = "createdDate", sortDirection = "DESC") => {
    // NOTE: the server dropped the companyId query param during the IDOR/company-scoping sweep
    // (scoping is now principal-derived), so it is no longer sent — matches the current spec.
    const query = {page, sortKey, sortDirection};
    if (keyword != null) query.keyword = keyword;
    if (trainer !== 0 && trainer != null) query.trainer = trainer;

    const {data, error, response} = await adminProgramControllerGetPendingPrograms({query});
    if (error) throw asAxiosError(error, response);
    return {data};
};

export const approvePendingProgram = async (pendingId) => {
    const {data, error, response} = await adminProgramControllerApprovePending({path: {pendingId}});
    if (error) throw asAxiosError(error, response);
    return {data};
};

export const finishWorkout = async (workoutId) => {
    const {data, error, response} = await adminProgramControllerFinishWorkout({path: {workoutId}});
    if (error) throw asAxiosError(error, response);
    return {data};
};

export const resetWeek = async (weekId) => {
    const {data, error, response} = await adminProgramControllerResetWeek({path: {weekId}});
    if (error) throw asAxiosError(error, response);
    return {data};
};

export const rerunManipulation = async (traineeId, programId, weekId) => {
    const {data, error, response} = await adminProgramControllerRunManipulation({path: {traineeId, programId, weekId}});
    if (error) throw asAxiosError(error, response);
    return {data};
};

/**
 * Assign a template to multiple trainees (bulk operation)
 * @param {number} templateId - The template ID to assign
 * @param {number[]} traineeIds - Array of trainee IDs
 * @param {boolean} [includeRmWeek] - Whether to include RM week (default: false)
 * @param {{automatic?: boolean, useProgressionAlgorithm?: boolean, useRir?: boolean} | null} [advancedSettings] - Advanced settings
 * @returns {Promise} - Results of the assignments
 */
export const assignTemplateToTrainees = async (templateId, traineeIds, includeRmWeek = false, advancedSettings = null) => {
    // Old code returned `response.data` (the payload), and callers read successCount/failureCount
    // off it directly. The SDK's `data` IS that payload, so return `data`.
    const {data, error, response} = await adminProgramControllerBulkChangeTraineeProgram({
        body: {
            templateId,
            traineeIds,
            includeRmWeek,
            automaticProgression: advancedSettings?.automatic ?? true,
            useProgressionAlgorithm: advancedSettings?.useProgressionAlgorithm ?? true,
            showRir: advancedSettings?.useRir ?? true,
        },
    });
    if (error) throw asAxiosError(error, response);
    return data;
};

export const getWeekStatistics = async (weekId) => {
    // Response is the HttpResponse envelope; caller unwraps `res.data.body ?? res.data`.
    const {data, error, response} = await adminProgramControllerGetWeekStatistics({path: {weekId}});
    if (error) throw asAxiosError(error, response);
    return {data};
};

export const updateProgramSettings = async (traineeId, settings) => {
    const {data, error, response} = await adminProgramControllerUpdateProgramSettings({
        path: {traineeId},
        body: settings,
    });
    if (error) throw asAxiosError(error, response);
    return {data};
};

/**
 * Update the warmup copy on an ALREADY-ASSIGNED program.
 *
 * Keyed by programId, not traineeId like updateProgramSettings above — the plan screens let a coach
 * open a historical (non-active) program, and the settings endpoint resolves the ACTIVE program only.
 * The warmup text is snapshotted from the template at assignment time, so editing the template does
 * NOT change what a trainee is currently seeing; this is the only way to fix that.
 *
 * PATCH semantics on the server: a null field is left untouched, an empty string clears that
 * language (the mobile app then hides the warmup body).
 *
 * @param {number} programId
 * @param {{warmupText?: string, warmupTextHeb?: string}} warmup
 */
export const updateProgramWarmup = async (programId, warmup) => {
    const {data, error, response} = await adminProgramControllerUpdateProgramWarmup({
        path: {programId},
        body: warmup,
    });
    if (error) throw asAxiosError(error, response);
    return {data};
};

/**
 * Get count of trainees matching filters (for Select All confirmation)
 * @param {number} templateId - The template ID
 * @param {string} gender - Gender filter ('ALL', 'MALE', 'FEMALE')
 * @param {string} keyword - Search keyword
 * @param {number} trainerId - Trainer ID filter
 * @param {number} companyId - Company ID filter
 * @returns {Promise} - Response with count
 */
export const getFilteredTraineeCount = async (templateId, gender, keyword, trainerId, companyId) => {
    // NOTE: companyId is no longer a server param (dropped in the company-scoping sweep); the
    // signature keeps it for caller compatibility but it is not sent. Caller reads response.data.count.
    const query = {templateId};
    if (gender && gender !== 'ALL') query.gender = gender;
    if (keyword) query.keyword = keyword;
    if (trainerId && trainerId !== 0) query.trainerId = trainerId;

    const {data, error, response} = await adminProgramControllerGetBulkAssignCount({query});
    if (error) throw asAxiosError(error, response);
    return {data};
};

/**
 * Assign template to ALL trainees matching filters (server-side selection)
 * @param {number} templateId - The template ID to assign
 * @param {{gender?: string, keyword?: string, trainerId?: number, companyId?: number}} filters - Filter criteria
 * @param {boolean} [includeRmWeek] - Whether to include RM week
 * @param {{automatic?: boolean, useProgressionAlgorithm?: boolean, useRir?: boolean} | null} [advancedSettings] - Advanced settings
 * @returns {Promise} - Results of the assignments
 */
export const assignTemplateToAllFiltered = async (templateId, filters, includeRmWeek = false, advancedSettings = null) => {
    // Old code returned `response.data` (the payload); callers read successCount/failureCount off it.
    // The old body sent explicit nulls for unset filters; the DTO's filter fields are optional-not-
    // nullable, so we omit them instead (server-equivalent: absent === "no filter").
    const body = {
        templateId,
        includeRmWeek,
        automaticProgression: advancedSettings?.automatic ?? true,
        useProgressionAlgorithm: advancedSettings?.useProgressionAlgorithm ?? true,
        showRir: advancedSettings?.useRir ?? true,
    };
    if (filters.gender && filters.gender !== 'ALL') body.gender = filters.gender;
    if (filters.keyword) body.keyword = filters.keyword;
    if (filters.trainerId && filters.trainerId !== 0) body.trainerId = filters.trainerId;
    if (filters.companyId) body.companyId = filters.companyId;

    const {data, error, response} = await adminProgramControllerBulkAssignToAllFiltered({body});
    if (error) throw asAxiosError(error, response);
    return data;
};

/**
 * Add a new workout day to all non-finished weeks in a program
 * @param {number} programId - The program ID
 * @param {string | null} [name] - Optional workout name (defaults to "Workout X")
 * @param {string | null} [nameHeb] - Optional Hebrew workout name
 * @returns {Promise} - Response with newNumberOfWorkouts, activeWorkoutIndexes, weeksModified
 */
export const addWorkoutDay = async (programId, name = null, nameHeb = null) => {
    const body = {programId};
    if (name) body.name = name;
    if (nameHeb) body.nameHeb = nameHeb;
    const {data, error, response} = await adminProgramControllerAddWorkoutDay({body});
    if (error) throw asAxiosError(error, response);
    return {data};
};

/**
 * Remove a workout day from all non-finished, non-completed weeks in a program.
 * Prefer passing groupUuid (stable across weeks); workoutIndex is a fallback for older callers.
 */
export const removeWorkoutDay = async (programId, groupUuid, workoutIndex) => {
    const {data, error, response} = await adminProgramControllerRemoveWorkoutDay({
        body: {programId, groupUuid, workoutIndex},
    });
    if (error) throw asAxiosError(error, response);
    return {data};
};

/**
 * Move a workout day to a new display index, applied to fromWeekId and every later non-finished week.
 */
export const moveWorkoutDay = async (programId, groupUuid, targetIndex, fromWeekId) => {
    const {data, error, response} = await adminProgramControllerMoveWorkoutDay({
        body: {programId, groupUuid, targetIndex, fromWeekId},
    });
    if (error) throw asAxiosError(error, response);
    return {data};
};
